import React, { ReactNode, createContext, useContext, useEffect, useMemo } from 'react';
import { format } from 'date-fns';

import { ForecastsView } from '@futrli/views/views/forecasts/ForecastsView/ForecastsView';
import { matchOrgDetailPath } from '@futrli/shared-services';
import { useApiView } from '@futrli/views/hooks/useApiView';

import { convertDDMMYYYtoYYYYMMDD } from '../../../utils/dates';
import { ForecastKindOption } from '../types';
import { NewForecastDataSource } from '../../dashboard/types';
import { useCreateForecastForm } from '../hooks/useCreateForecastForm';
import { useExcelForecastUploadField } from '../hooks/useExcelForecastUploadField';
import { useGenerateExcelForecastTemplate } from '../hooks/useGenerateExcelForecastTemplate';

interface CreateForecastWizardContextProps {
  children?: ReactNode;
  kind: ForecastKindOption;
}

interface CreateForecastWizardContextValues {
  excelField: ReturnType<typeof useExcelForecastUploadField>;
  excelTemplate: ReturnType<typeof useGenerateExcelForecastTemplate>;
  form: ReturnType<typeof useCreateForecastForm>;
  isExcelUpload: boolean;
  kind: ForecastKindOption;
  view: ForecastsView;
}

const defaultState = ({} as unknown) as CreateForecastWizardContextValues;
export const CreateForecastWizardContext = createContext(defaultState);

export const CreateForecastWizardContextProvider = ({ children, kind }: CreateForecastWizardContextProps) => {
  const match = matchOrgDetailPath(window.location.pathname, false);
  const workspaceId = match?.params?.workspaceId || '';
  const organisationId = match?.params?.organisationId || '';

  const view = useApiView(useMemo(() => new ForecastsView(workspaceId, organisationId), [workspaceId, organisationId]));
  const { createForecast } = view;
  const { datasourceModifiedDate, organisationCreated } = view.state;

  const form = useCreateForecastForm(kind, {}, organisationCreated);
  const fromYYYYMMDD = convertDDMMYYYtoYYYYMMDD(form.from);
  const isExcelUpload = form.datasource === NewForecastDataSource.FromExcelUpload;

  const excelField = useExcelForecastUploadField();
  const excelTemplate = useGenerateExcelForecastTemplate(workspaceId, organisationId, kind, fromYYYYMMDD);

  const { from, setFrom, type } = form;
  const { data: excelTemplateData, reset: resetExcelTemplate } = excelTemplate;

  // Set the 'from' date to the last modified date of the datasource if forecast type is 'rolling'
  useEffect(() => {
    if (type === 'rolling' && datasourceModifiedDate) {
      setFrom(format(Date.parse(datasourceModifiedDate), 'dd/MM/yyyy'));
    } else {
      setFrom('');
      resetExcelTemplate();
    }
  }, [datasourceModifiedDate, resetExcelTemplate, type, setFrom]);

  // If we dont have a valid from date, wipe out the excel template url
  useEffect(() => {
    if (excelTemplateData && !convertDDMMYYYtoYYYYMMDD(from)) {
      resetExcelTemplate();
    }
  }, [excelTemplateData, from, resetExcelTemplate]);

  const values = {
    createForecast,
    excelField,
    excelTemplate,
    form,
    isExcelUpload,
    kind,
    type,
    view
  };

  return <CreateForecastWizardContext.Provider value={values}>{children}</CreateForecastWizardContext.Provider>;
};

export const useCreateForecastWizardContext = () => useContext(CreateForecastWizardContext);
